import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import type { HseHistoryChange, HseHistoryEvent, HseWeekSummary } from '../../types/hse-weekly-report.type';
import { formatDateTime, formatWeekRange, isolate } from '../../utils/hseFormat';
import { useNumberFormat } from '../../hooks/useNumberFormat';

export type HseDrawerTab = 'history' | 'weeks';

export interface HseHistoryDrawerProps {
  open: boolean;
  tab: HseDrawerTab;
  onTabChange: (tab: HseDrawerTab) => void;
  onClose: () => void;
  weekLabel: string;
  history: HseHistoryEvent[];
  weeks: HseWeekSummary[];
  loadingHistory: boolean;
  loadingWeeks: boolean;
  selectedWeekStart: string;
  onOpenWeek: (weekStart: string) => void;
}

const HseHistoryDrawer: React.FC<HseHistoryDrawerProps> = ({
  open,
  tab,
  onTabChange,
  onClose,
  weekLabel,
  history,
  weeks,
  loadingHistory,
  loadingWeeks,
  selectedWeekStart,
  onOpenWeek,
}) => {
  const { t, i18n } = useTranslation();
  const fmt = useNumberFormat();

  const describeChange = (change: HseHistoryChange): string => {
    const subject = change.term_key ? t(`hse.terms.${change.term_key}.label`) : t(`hse.history.fields.${change.field}`);
    if (change.field === 'notes') {
      return t('hse.history.notesChanged', { term: subject });
    }
    const show = (v: string | number | null) => (v === null || v === '' ? '—' : fmt.number(Number(v)));
    return t('hse.history.valueChanged', { field: subject, from: show(change.from), to: show(change.to) });
  };

  const renderEvent = (event: HseHistoryEvent) => {
    const details = event.details ?? {};
    const lines: string[] = [];
    if (details.changes?.length) lines.push(...details.changes.map(describeChange));
    if (details.reason) lines.push(t('hse.history.reason', { reason: details.reason }));
    if (details.files?.length) lines.push(...details.files.map((f) => f.name ?? '—'));
    return (
      <Box key={event.id} sx={{ py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t(`hse.history.actions.${event.action}`, { defaultValue: event.action })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isolate(formatDateTime(event.created_at, i18n.language))}
            {event.actor_username ? ` · ${isolate(event.actor_username)}` : ''}
          </Typography>
        </Box>
        {lines.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: 0.5, paddingInlineStart: 2.5 }}>
            {lines.map((line, i) => (
              <Typography key={i} component="li" variant="body2" color="text.secondary">
                {line}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, pt: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
          {t('hse.title')}
        </Typography>
        <IconButton onClick={onClose} aria-label={t('common.close')}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Tabs value={tab} onChange={(_, v: HseDrawerTab) => onTabChange(v)} sx={{ px: 1.5 }}>
        <Tab value="history" label={t('hse.history.tab')} />
        <Tab value="weeks" label={t('hse.weeks.tab')} />
      </Tabs>
      <Divider />
      <Box sx={{ px: 2.5, py: 1, overflowY: 'auto' }}>
        {tab === 'history' ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {weekLabel}
            </Typography>
            {loadingHistory ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : history.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                {t('hse.history.empty')}
              </Typography>
            ) : (
              history.map((event, i) => (
                <React.Fragment key={event.id}>
                  {i > 0 && <Divider />}
                  {renderEvent(event)}
                </React.Fragment>
              ))
            )}
          </>
        ) : loadingWeeks ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : weeks.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
            {t('hse.weeks.empty')}
          </Typography>
        ) : (
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('hse.weeks.week')}</TableCell>
                <TableCell>{t('hse.weeks.status')}</TableCell>
                <TableCell align="right">{t('hse.weeks.incidents')}</TableCell>
                <TableCell align="right">{t('hse.weeks.hours')}</TableCell>
                <TableCell align="right">{t('hse.weeks.files')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {weeks.map((week) => (
                <TableRow
                  key={week.week_start}
                  hover
                  selected={week.week_start === selectedWeekStart}
                  onClick={() => onOpenWeek(week.week_start)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatWeekRange(week.week_start, i18n.language)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color="primary"
                      variant={week.status === 'submitted' ? 'filled' : 'outlined'}
                      label={week.status === 'submitted' ? t('hse.status.submitted') : t('hse.status.draft')}
                    />
                  </TableCell>
                  <TableCell align="right">{fmt.number(week.total_incidents)}</TableCell>
                  <TableCell align="right">{week.hours_worked === null ? '—' : fmt.number(week.hours_worked)}</TableCell>
                  <TableCell align="right">{fmt.number(week.evidence_count)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Drawer>
  );
};

export default HseHistoryDrawer;
